"use client";

import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import { createApiClient, type ApiClientConfig } from "./client";

type ApiClient = ReturnType<typeof createApiClient>;
const ApiClientContext = createContext<ApiClient | null>(null);

export const CART_TOKEN_STORAGE_KEY = "ntr_cart_token";

/** Persist the backend-issued cart token so subsequent requests carry it (guest carts). */
export function persistCartToken(token: string) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(CART_TOKEN_STORAGE_KEY, token);
}

interface AuthHolder {
  getToken: () => string | undefined;
  setToken: (token: string | undefined) => void;
}

const AuthHolderContext = createContext<AuthHolder | null>(null);

export interface ApiClientProviderProps {
  children: ReactNode;
  baseUrl: string;
  tenantCode?: string;
  defaultLocale?: string;
  cartTokenStorageKey?: string;
}

export function ApiClientProvider({
  children,
  baseUrl,
  tenantCode = "nutrayan",
  defaultLocale = "en-IN",
  cartTokenStorageKey = "ntr_cart_token",
}: ApiClientProviderProps) {
  const [token, setToken] = useState<string | undefined>(undefined);

  const auth = useMemo<AuthHolder>(() => ({ getToken: () => token, setToken }), [token]);

  const client = useMemo<ApiClient>(() => {
    const config: ApiClientConfig = {
      baseUrl,
      tenantCode,
      defaultLocale,
      getAuthToken: () => auth.getToken(),
      getCartToken: () => {
        if (typeof window === "undefined") return undefined;
        return window.localStorage.getItem(cartTokenStorageKey) ?? undefined;
      },
    };
    return createApiClient(config);
  }, [baseUrl, tenantCode, defaultLocale, auth, cartTokenStorageKey]);

  return (
    <ApiClientContext.Provider value={client}>
      <AuthHolderContext.Provider value={auth}>{children}</AuthHolderContext.Provider>
    </ApiClientContext.Provider>
  );
}

export function useApiClient() {
  const ctx = useContext(ApiClientContext);
  if (!ctx) throw new Error("useApiClient must be used inside <ApiClientProvider>");
  return ctx;
}

export function useAuthToken() {
  const ctx = useContext(AuthHolderContext);
  if (!ctx) throw new Error("useAuthToken must be used inside <ApiClientProvider>");
  return ctx;
}
